import {DataStatus, Segment} from 'platform/components';
import {Scroll, VStack} from 'platform/foundation';

import {useState} from 'react';

import {ascend, isNotNil, sortWith} from 'ramda';

import {CheckoutMessageLogEntryResponseBody, useGetCheckoutMessageLogQuery} from '@dms/api/core';
import i18n from '@dms/i18n';

import {parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {CheckoutMessageLogEntry} from './CheckoutMessageLogEntry';

export interface CheckoutMessageLogDialogProps extends TestIdProps {
  checkoutId: string;
}

type CheckoutMessageLogViewMode = 'overview' | 'raw';

const viewModeOptions: {value: CheckoutMessageLogViewMode; label: string}[] = [
  {value: 'overview', label: i18n.t('general.labels.overview')},
  {value: 'raw', label: i18n.t('entity.checkout.labels.messageLogRaw')},
];

const sortByRecordedAt = sortWith<CheckoutMessageLogEntryResponseBody>([
  ascend((entry) => parseDate(entry.recordedAt)),
]);

export function CheckoutMessageLogDialog(props: CheckoutMessageLogDialogProps) {
  const [viewMode, setViewMode] = useState<CheckoutMessageLogViewMode>('overview');

  const {data, isLoading, isError} = useGetCheckoutMessageLogQuery(
    {checkoutId: props.checkoutId},
    {refetchOnMountOrArgChange: true}
  );

  const sortedMessages = isNotNil(data) ? sortByRecordedAt(data.messages) : undefined;

  return (
    <VStack spacing={4}>
      <Segment
        value={viewMode}
        options={viewModeOptions}
        onChange={setViewMode}
        data-testid={suffixTestId('viewMode', props)}
      />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
        {viewMode === 'overview' ? (
          <VStack spacing={2}>
            {sortedMessages?.map((entry) => (
              <CheckoutMessageLogEntry
                key={entry.id}
                entry={entry}
                data-testid={suffixTestId(entry.id, props)}
              />
            ))}
          </VStack>
        ) : (
          <Scroll auto maxHeight={120}>
            <pre data-testid={suffixTestId('messageLogRaw', props)}>
              {JSON.stringify(isNotNil(data) ? {...data, messages: sortedMessages} : data, null, 2)}
            </pre>
          </Scroll>
        )}
      </DataStatus>
    </VStack>
  );
}
